"use client";

import Image from "next/image";

import { usePageContext } from "@repo/ui/contexts/PageContext";
import type { HomePageContent } from "@repo/ui/types";

import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";
import BenefitCard from "./BenefitCard";

export interface AllianceCardItem {
  addBorder: boolean;
  contentAlignment: string;
  description: string;
  imageUrl: string;
  link: string;
  linkText: string;
  title: string;
}

export interface AllianceCards {
  cards: AllianceCardItem[];
  ctaLink: string;
  ctaText: string;
  headlineHtml: string;
  icon: string;
}

export default function AllianceBenefits() {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();
  const { allianceCards } = normalizedSections;

  if (!allianceCards) return null;

  const { cards, headlineHtml, icon } = allianceCards;

  return (
    <section
      className="responsive-padding responsive-layout w-full"
      data-testid="alliance-benefits-section"
    >
      <div className="border-brand-cyan mb-10 flex items-center gap-1 border-l-3 p-2 pl-6">
        <Image src={icon} width={28} height={28} alt="Alliance icon" />

        <h2
          className="flex items-center text-lg font-semibold text-gray-800 lg:text-3xl leading-[36px]"
          dangerouslySetInnerHTML={{ __html: headlineHtml }}
        />
      </div>

      <div
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="alliance-benefits-list"
      >
        {cards.map((item) => (
          <BenefitCard
            key={item.title}
            imageSrc={item.imageUrl}
            alt={item.title}
            title={item.title}
            description={item.description}
            link={item.link}
            linkText={item.linkText}
          />
        ))}
      </div>
    </section>
  );
}
